import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Search, ChevronLeft, Share2, BookOpen, Clock, CheckCircle2 } from 'lucide-react';
import { renderAlgorithmContent, renderJSContent, renderPythonContent } from './Content';
import { renderJavaContent } from './SubContent';

const SubTutorial = () => {
  const { id } = useParams();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeTopic, setActiveTopic] = useState('');

  // 1. DSA Menu (Existing)
  const dsaMenu = [
    { id: 1, title: "INTRODUCTION", topics: ["Introduction"] },
    { id: 2, title: "COMPLEXITY ANALYSIS", topics: ["Asymptotic Analysis"] },
    { id: 3, title: "ARRAYS", topics: ["Array Basics", "2D & Multi-Dimensional Arrays"] },
    { id: 4, title: "LINKED LISTS", topics: ["Introduction to Linked Lists", "Singly Linked List", "Doubly Linked List", "Circular Linked List"] },
    { id: 5, title: "STACK & QUEUE", topics: ["Stack", "Queue"] },
    { id: 6, title: "TREES", topics: ["Binary Tree", "Binary Search Tree", "Tree Traversals", "Heaps (Min/Max)"] },
    { id: 7, title: "GRAPHS", topics: ["Introduction to Graphs", "BFS Traversal", "DFS Traversal"] },
    { id: 8, title: "HASHING", topics: ["Hashing"] }
  ];

  // 2. Algorithms Menu (Existing)
  const algorithmsMenu = [
    { id: 1, title: "ALGORITHM BASICS", topics: ["What is an Algorithm", "Time & Space Complexity"] },
    { id: 2, title: "MATHEMATICS FOR ALGORITHMS", topics: ["Prime Numbers & Sieve", "GCD & LCM", "Modular Arithmetic"] },
    { id: 3, title: "ARRAYS & STRINGS ALGORITHMS", topics: ["Two Pointer Technique", "Sliding Window", "Kadane's Algorithm", "KMP Algorithm", "Dutch National Flag"] },
    { id: 4, title: "SEARCHING ALGORITHMS", topics: ["Linear & Binary Search", "Ternary Search"] },
    { id: 5, title: "SORTING ALGORITHMS", topics: ["Basic Sorts (Bubble, Insertion, Selection)", "Advanced Sorts (Merge, Quick)", "Linear Sorts (Counting, Radix)"] },
    { id: 6, title: "RECURSION & BACKTRACKING", topics: ["Recursion Basics", "N-Queens Problem", "Rat in a Maze"] },
    { id: 7, title: "DIVIDE & CONQUER", topics: ["Divide & Conquer Strategy"] },
    { id: 8, title: "GREEDY ALGORITHMS", topics: ["Greedy Algorithms"] },
    { id: 9, title: "DYNAMIC PROGRAMMING", topics: ["DP Concepts", "Common DP Problems"] },
    { id: 10, title: "TREE ALGORITHMS", topics: ["Tree Basics & Traversals", "Advanced Tree Structures"] },
    { id: 11, title: "GRAPH ALGORITHMS", topics: ["BFS & DFS", "Topological Sort", "Strongly Connected Components"] },
    { id: 12, title: "SHORTEST PATH ALGORITHMS", topics: ["Dijkstra & Bellman-Ford", "Floyd-Warshall"] },
    { id: 13, title: "MINIMUM SPANNING TREE", topics: ["Prim & Kruskal"] },
    { id: 14, title: "NETWORK FLOW", topics: ["Max Flow"] },
    { id: 15, title: "ADVANCED DATA STRUCTURES", topics: ["Disjoint Set (Union-Find)"] },
    { id: 16, title: "BIT MANIPULATION", topics: ["Bitwise Tricks"] },
    { id: 17, title: "STRING ALGORITHMS", topics: ["Advanced String Algos"] },
    { id: 18, title: "GEOMETRIC ALGORITHMS", topics: ["Convex Hull"] },
    { id: 19, title: "GAME THEORY", topics: ["Minimax & Nim"] },
    { id: 20, title: "ML SPECIFIC ALGORITHMS", topics: ["Gradient Descent & variants", "Backpropagation", "K-Means Clustering", "Decision Trees & Random Forest", "PCA & SVM"] },
    { id: 21, title: "MISC / MODERN ALGORITHMS", topics: ["Bloom Filters", "Skip Lists & Ropes", "LRU Cache", "System Design Algos"] }
  ];

  // 3. JavaScript Menu (Existing)
  const jsMenu = [
    { id: 1, title: "BASICS / FUNDAMENTALS", topics: ["Introduction to JS", "Variables (var, let, const)", "Data Types", "Operators"] },
    { id: 2, title: "CONTROL FLOW", topics: ["if / else / else if", "switch", "Loops (for, while)"] },
    { id: 3, title: "FUNCTIONS", topics: ["Function Definition", "Arrow Functions"] },
    { id: 4, title: "ARRAYS", topics: ["Introduction to Arrays", "Common Array Methods"] },
    { id: 5, title: "STRINGS", topics: ["String Methods", "Template Literals"] },
    { id: 6, title: "DOM (DOCUMENT OBJECT MODEL)", topics: ["Selecting Elements", "Changing HTML/CSS"] },
    { id: 7, title: "EVENTS", topics: ["Common Event Types"] },
    { id: 8, title: "ASYNCHRONOUS JAVASCRIPT", topics: ["Callbacks", "Promises", "async/await"] },
    { id: 9, title: "ES6+ (MODERN JAVASCRIPT)", topics: ["let & const", "Template Strings", "Arrow Functions", "Classes", "Modules (import/export)", "Default Parameters", "Spread/Rest Operator (...)", "Destructuring"] },
    { id: 10, title: "ADVANCED TOPICS", topics: ["Closures", "Hoisting", "Prototype & Prototypal Inheritance", "The Event Loop"] },
    { id: 11, title: "ERROR HANDLING", topics: ["try / catch / finally", "Throw Statement", "Custom Errors", "Common Error Types"] },
    { id: 12, title: "OOP IN JAVASCRIPT", topics: ["Classes & Constructors", "Inheritance", "Polymorphism", "Encapsulation", "Abstraction", "Prototype Inheritance"] },
    { id: 13, title: "MODULES", topics: ["ES Modules (import/export)", "Dynamic Imports", "CJS vs ESM (Node.js)"] },
    { id: 14, title: "FETCHING DATA & APIS", topics: ["Fetch API", "JSON", "REST API & CRUD Operations", "Headers, Status Codes, CORS", "Postman Basics"] },
    { id: 15, title: "NODE.JS (OPTIONAL BUT IMPORTANT)", topics: ["npm & package.json", "Node modules", "File System (fs)", "Events", "Streams", "Express.js basics"] },
    { id: 16, title: "TOOLING", topics: ["Webpack", "Babel", "Vite", "ESLint & Prettier"] },
    { id: 17, title: "TESTING", topics: ["Jest", "Mocha/Chai", "Unit Testing Basics"] },
    { id: 18, title: "SECURITY", topics: ["XSS", "CSRF", "Sanitization", "Secure Cookies", "Token-based Auth (JWT)"] },
    { id: 19, title: "MODERN / LATEST TOPICS (2023-2025)", topics: ["Top-Level Await", "Optional Chaining (?.)", "Nullish Coalescing (??)", "Private Class Fields (#)", "Promise.any()", "WeakRef & FinalizationRegistry", "Web Workers", "Service Workers (PWA)", "WebRTC Basics", "WebSockets (real-time apps)", "Fetch Streams", "Temporal API", "Edge Functions"] },
    { id: 20, title: "FRAMEWORK BASICS", topics: ["React.js Basics", "Next.js Basics", "TypeScript"] }
  ];

  // 4. Python Menu (Existing)
  const pythonMenu = [
    { id: 1, title: "PYTHON BASICS", topics: ["What is Python?", "Installation & Setup", "Variables", "Data Types", "Input/Output", "Operators", "Type Casting", "Comments", "Indentation", "Keywords", "Basic Programs"] },
    { id: 2, title: "FLOW CONTROL", topics: ["if / elif / else", "Loops (for, while)", "break / continue / pass", "Pattern Printing"] },
    { id: 3, title: "DATA STRUCTURES", topics: ["Lists", "Tuples", "Sets", "Dictionaries", "Comprehensions", "Slicing"] },
    { id: 4, title: "STRINGS", topics: ["String Methods", "Formatting (f-strings)"] },
    { id: 5, title: "FUNCTIONS", topics: ["Function Definition", "Lambda Functions", "*args & **kwargs"] },
    { id: 6, title: "MODULES & PACKAGES", topics: ["import & Standard Library", "Creating Modules", "pip & PyPI", "Virtual Environments (venv)"] },
    { id: 7, title: "FILE HANDLING", topics: ["Read/Write Files", "Working with JSON", "Working with CSV"] },
    { id: 8, title: "EXCEPTION HANDLING", topics: ["try / except / finally", "raise & Custom Exceptions"] },
    { id: 9, title: "OBJECT-ORIENTED PROGRAMMING", topics: ["Classes & Objects", "Inheritance", "Encapsulation & Abstraction", "Polymorphism"] },
    { id: 10, title: "ADVANCED CONCEPTS", topics: ["Generators", "Decorators", "Collections Module"] },
    { id: 11, title: "DATA HANDLING & LIBRARIES", topics: ["NumPy", "Pandas", "Matplotlib & Seaborn", "Working with APIs"] },
    { id: 12, title: "DATABASE (DB)", topics: ["SQLite", "MySQL Connector"] },
    { id: 13, title: "WEB DEVELOPMENT", topics: ["Flask", "Django", "FastAPI"] },
    { id: 14, title: "AUTOMATION & SCRIPTING", topics: ["BeautifulSoup (Web Scraping)", "Selenium (Browser Automation)"] },
    { id: 15, title: "MACHINE LEARNING / AI BASICS", topics: ["Scikit-Learn"] },
    { id: 16, title: "DEVOPS + DEPLOYMENT", topics: ["Docker", "CI/CD Basics"] },
    { id: 17, title: "PYTHON TESTING", topics: ["pytest"] },
    { id: 18, title: "LATEST PYTHON TOPICS (2024-2025)", topics: ["Type Hints & pydantic", "async/await & asyncio", "Structural Pattern Matching (match-case)", "LLM Integrations (LangChain)"] },
    { id: 19, title: "COMPETITIVE PROGRAMMING", topics: ["Time & Space Complexity", "Dynamic Programming (DP)", "Two Pointers & Sliding Window"] }
  ];

  // 5. Java Menu (Existing)
  const javaMenu = [
    { id: 1, title: "BASICS / FUNDAMENTALS", topics: ["What is Java?", "JDK, JRE, and JVM", "Java Installation", "Syntax", "Variables", "Data Types", "Type Casting", "Operators", "Input/Output", "Comments"] },
    { id: 2, title: "CONTROL FLOW", topics: ["if / else", "switch", "loops (for, while, do-while)", "break / continue", "nested loops"] },
    { id: 3, title: "ARRAYS", topics: ["1D Arrays", "2D Arrays", "Array Methods", "Sorting", "Searching"] },
    { id: 4, title: "STRINGS", topics: ["String class", "StringBuilder", "StringBuffer", "String Methods", "Immutable vs Mutable"] },
    { id: 5, title: "FUNCTIONS / METHODS", topics: ["Method Declaration", "Parameters / Arguments", "Method Overloading", "Return Types", "Static vs Non-Static Methods", "Recursion"] },
    { id: 6, title: "OOP", topics: ["Classes & Objects", "Constructors", "Inheritance", "Polymorphism", "Encapsulation", "Abstraction", "Access Modifiers", "this & super", "Packages", "Interfaces", "Abstract Classes"] },
    { id: 7, title: "JAVA OOP ADVANCED", topics: ["final keyword", "static keyword", "inner classes", "anonymous inner class", "enums", "annotations", "Object class methods"] },
    { id: 8, title: "EXCEPTION HANDLING", topics: ["try / catch / finally", "throw / throws", "Custom Exceptions", "Exception Hierarchy"] },
    { id: 9, title: "COLLECTIONS FRAMEWORK", topics: ["List, Set, Map", "ArrayList", "LinkedList", "HashSet, LinkedHashSet", "HashMap, LinkedHashMap", "TreeSet, TreeMap", "Queue, PriorityQueue", "Iterator", "Comparable vs Comparator"] },
    { id: 10, title: "GENERICS", topics: ["Generic Classes", "Generic Methods", "Bounded Types", "Wildcards"] },
    { id: 11, title: "FILE HANDLING", topics: ["File Class", "FileReader / FileWriter", "BufferedReader / BufferedWriter", "InputStream / OutputStream", "Serialization & Deserialization"] },
    { id: 12, title: "MULTI-THREADING", topics: ["Thread class & Runnable interface", "Thread States", "Synchronization", "Thread Pool (ExecutorService)", "Callable & Future"] },
    { id: 13, title: "JAVA 8 FEATURES", topics: ["Lambda Expressions", "Functional Interfaces", "Stream API", "Optional Class", "Method References", "Date & Time API"] },
    { id: 14, title: "JAVA 9-11 FEATURES", topics: ["Modular System (Java Modules)", "JShell", "HTTP Client API", "Local-Variable Syntax (var)"] },
    { id: 15, title: "JAVA 12-17 FEATURES", topics: ["Switch Expressions", "Text Blocks", "Records", "Sealed Classes", "Pattern Matching for instanceof"] },
    { id: 16, title: "JAVA 18-21 FEATURES", topics: ["Virtual Threads (Project Loom)", "Structured Concurrency", "Pattern Matching for switch", "String Templates"] },
    { id: 17, title: "LATEST JAVA (22+)", topics: ["Unnamed Classes & Instance Main Methods", "Scoped Values (Project Loom)", "Reactive Programming (Reactor, RxJava)", "Spring AI", "GraalVM Native Images"] },
    { id: 18, title: "JAVA NETWORKING", topics: ["Sockets", "HttpClient API"] },
    { id: 19, title: "JDBC (DATABASE)", topics: ["JDBC Driver & Connection", "CRUD with PreparedStatement", "Transactions"] },
    { id: 20, title: "BUILD TOOLS", topics: ["Maven", "Gradle"] },
    { id: 21, title: "TESTING", topics: ["JUnit", "Mockito"] },
    { id: 22, title: "ENTERPRISE FRAMEWORKS", topics: ["Spring Boot", "Spring MVC & REST APIs", "Spring Data JPA / Hibernate", "Microservices", "Spring Security"] }
  ];

  // 6. C Programming Menu (Exact from screenshots)
  const cMenu = [
    { id: 1, title: "C BASICS", topics: ["Introduction to C", "Variables & Data Types", "Input/Output (printf/scanf)", "Operators"] },
    { id: 2, title: "CONTROL FLOW", topics: ["if...else & Switch", "Loops (for, while, do-while)"] },
    { id: 3, title: "FUNCTIONS", topics: ["Functions & Recursion", "Scope (Local vs Global)"] },
    { id: 4, title: "ARRAYS & STRINGS", topics: ["Arrays (1D & 2D)", "Strings"] },
    { id: 5, title: "POINTERS", topics: ["Introduction to Pointers", "Pointer Arithmetic", "Pointers & Arrays"] },
    { id: 6, title: "MEMORY MANAGEMENT", topics: ["Dynamic Memory Allocation"] },
    { id: 7, title: "STRUCTS & UNIONS", topics: ["Structures (struct)", "Unions"] },
    { id: 8, title: "FILE HANDLING", topics: ["File I/O (fopen, fprintf)"] },
    { id: 9, title: "DATA STRUCTURES IMPLEMENTATION", topics: ["Singly Linked List Implementation"] },
    { id: 10, title: "ADVANCED MEMORY", topics: ["Dynamic Arrays (malloc)"] }
  ];

  const cppMenu = [
    { 
      id: 1, 
      title: "C++ BASICS", 
      topics: ["Introduction to C++", "Input/Output (cin/cout)"] 
    },
    { 
      id: 2, 
      title: "FUNCTIONS", 
      topics: ["Functions & Overloading", "References"] 
    },
    { 
      id: 3, 
      title: "OOP", 
      topics: ["Classes & Objects", "Encapsulation", "Inheritance", "Polymorphism (Virtual Functions)"] 
    },
    { 
      id: 4, 
      title: "ADVANCED CONCEPTS", 
      topics: ["Templates (Generic Programming)", "Exception Handling", "Memory Management (new/delete)"] 
    },
    { 
      id: 5, 
      title: "STL", 
      topics: ["Intro to STL", "Vectors (Dynamic Arrays)", "Lists (Doubly Linked List)", "Custom Singly Linked List (OOP)", "Maps (Dictionary)"] 
    },
    { 
      id: 6, 
      title: "MEMORY MANAGEMENT", 
      topics: ["Memory Management (new/delete)"] 
    },
    { 
      id: 6, 
      title: "ADVANCED EXCEPTION HANDLING", 
      topics: ["Advanced Exception Handling", "Creating Custom Exceptions"] 
    }
  ];

 // 8. PHP Menu (Exact from your screenshots)
  const phpMenu = [
    { id: 1, title: "PHP BASICS", topics: ["What is PHP?", "Installation (XAMPP/WAMP)", "Syntax & PHP Tags", "Variables & Data Types", "Echo vs. Print"] },
    { id: 2, title: "CONTROL FLOW", topics: ["if / else / elseif", "switch", "Loops (for, while, do-while)"] },
    { id: 3, title: "FUNCTIONS", topics: ["User-Defined Functions"] },
    { id: 4, title: "ARRAYS", topics: ["Indexed & Associative Arrays"] },
    { id: 5, title: "STRINGS", topics: ["String Functions", "String Interpolation"] },
    { id: 6, title: "FORMS & USER INPUT", topics: ["GET & POST Methods", "Form Handling & Validation"] },
    { id: 7, title: "SUPERGLOBALS", topics: ["Superglobals"] },
    { id: 8, title: "COOKIES & SESSIONS", topics: ["Cookies & Sessions"] },
    { id: 9, title: "FILE HANDLING", topics: ["File Read/Write"] },
    { id: 10, title: "DATABASES", topics: ["PDO (PHP Data Objects)", "CRUD with PDO"] },
    { id: 11, title: "PHP OOP", topics: ["Classes & Objects", "Inheritance", "Encapsulation (Access Modifiers)", "Abstraction & Interfaces", "Namespaces & Autoloading"] },
    { id: 12, title: "ADVANCED CONCEPTS", topics: ["Composer (Package Manager)", "Design Patterns (MVC)", "Error Handling & Exceptions"] },
    { id: 13, title: "MODERN PHP 8+", topics: ["PHP 8.0 Features", "PHP 8.1 Features", "PHP 8.2+ Features"] },
    { id: 14, title: "PHP SECURITY", topics: ["SQL Injection Prevention", "XSS Protection", "Password Hashing"] },
    { id: 15, title: "FRAMEWORKS", topics: ["Laravel (Most Important)"] },
    { id: 16, title: "APIS", topics: ["REST API in PHP"] },
    { id: 17, title: "PHP + AJAX/JS", topics: ["PHP with AJAX (Fetch API)"] },
    { id: 19, title: "PHP TESTING", topics: ["PHPUnit"] },
    { id: 20, title: "LATEST & TRENDING", topics: ["Trending Topics (2024-2025)"] }
  ];

 const mlMenu = [
    { id: 1, title: "INTRODUCTION", topics: ["What is Machine Learning?", "Types of Machine Learning"] },
    { id: 2, title: "CORE CONCEPTS", topics: ["Data Representation", "The Learning Process", "Overfitting vs Underfitting", "Data Splitting"] },
    { id: 3, title: "PYTHON LIBRARIES", topics: ["NumPy for Numerical Data", "Pandas for Data Manipulation", "Matplotlib for Visualization"] },
    { id: 4, title: "SUPERVISED LEARNING", topics: ["Linear Regression", "Logistic Regression", "Decision Trees"] },
    { id: 5, title: "UNSUPERVISED LEARNING", topics: ["K-Means Clustering"] },
    { id: 6, title: "MODEL EVALUATION", topics: ["Classification Metrics", "Regression Metrics", "Bias-Variance & Overfitting"] },
    { id: 7, title: "ADVANCED SUPERVISED LEARNING", topics: ["Random Forest", "Gradient Boosting & XGBoost", "Support Vector Machines (SVM)"] },
    { id: 8, title: "ADVANCED UNSUPERVISED LEARNING", topics: ["Hierarchical Clustering", "DBSCAN", "PCA (Dimensionality Reduction)"] },
    { id: 9, title: "FEATURE ENGINEERING", topics: ["Handling Missing Data", "Categorical Encoding", "Feature Scaling"] },
    { id: 10, title: "DATA PREPROCESSING", topics: ["Data Cleaning", "Pipeline Creation", "Handling Imbalance (SMOTE)"] },
    { id: 11, title: "MODEL OPTIMIZATION", topics: ["Hyperparameter Tuning", "Grid Search vs Random Search", "Bayesian Optimization", "Cross-Validation", "Bias-Variance Tradeoff"] },
    { id: 12, title: "TIME SERIES ANALYSIS", topics: ["Stationarity", "ARIMA & SARIMA", "Decomposition", "LSTM for Forecasting"] },
    { id: 13, title: "NLP", topics: ["Tokenization & Preprocessing", "TF-IDF", "Word Embeddings (Word2Vec/GloVe)", "Transformers & Attention"] },
    { id: 14, title: "DEEP LEARNING", topics: ["Neural Network Architecture", "Activation Functions"] },
    { id: 15, title: "COMPUTER VISION", topics: ["CNN Architecture", "Pooling & Padding", "Object Detection (YOLO)", "Data Augmentation"] },
    { id: 16, title: "DEEP LEARNING FRAMEWORKS", topics: ["TensorFlow & Keras", "PyTorch", "HuggingFace Transformers"] },
    { id: 17, title: "BIG DATA & DISTRIBUTED PROCESSING", topics: ["Hadoop Ecosystem", "Apache Spark"] },
    { id: 18, title: "MLOPS", topics: ["Model Deployment", "Tracking & Versioning (MLflow & DVC)", "Model Monitoring"] },
    { id: 19, title: "DATA ENGINEERING BASICS", topics: ["ETL Concepts", "SQL for Data Science", "Airflow & Orchestration"] },
    { id: 20, title: "DATA VISUALIZATION (ADVANCED)", topics: ["Seaborn Internals", "Interactive Viz (Plotly/Dash)", "BI Workflow (Tableau/PowerBI)"] },
    { id: 21, title: "STATISTICS & MATH FOR ML", topics: ["Probability & Bayes", "Probability Distributions", "Hypothesis Testing", "p-values & Confidence Intervals", "Correlation & Covariance", "Linear Algebra Basics", "Matrix Operations", "Calculus (Gradients)"] },
    { id: 22, title: "REINFORCEMENT LEARNING", topics: ["Agent & Environment", "Q-Learning", "Deep Q-Learning (DQN)"] },
    { id: 23, title: "END-TO-END ML PIPELINE", topics: ["Data Collection", "Data Cleaning", "Exploratory Data Analysis (EDA)", "Feature Engineering", "Deployment Strategies", "Monitoring & Maintenance"] },
    { id: 24, title: "EXPLAINABLE AI (XAI)", topics: ["Model Explainability (XAI)"] },
    { id: 25, title: "ETHICS & GOVERNANCE", topics: ["Interpretability & Ethics"] },
    { id: 26, title: "DATA ANNOTATION", topics: ["Data Annotation Strategy"] },
    { id: 27, title: "FEATURE STORES", topics: ["Feature Store Concepts"] },
    { id: 28, title: "CLOUD MACHINE LEARNING", topics: ["Cloud Machine Learning"] },
    { id: 29, title: "AUTOML", topics: ["AutoML (2024-2025 Trend)"] },
    { id: 30, title: "TRANSFER LEARNING", topics: ["Transfer Learning"] },
    { id: 31, title: "ADVANCED OPTIMIZATION", topics: ["Advanced Optimization"] },
    { id: 32, title: "GENERATIVE AI", topics: ["Generative AI (2024-2025)"] },
    { id: 33, title: "VECTOR DATABASES", topics: ["Vector Databases"] },
    { id: 34, title: "RAG", topics: ["Retrieval-Augmented Generation (RAG)"] },
    { id: 35, title: "DATA QUALITY", topics: ["Data Quality & Governance"] },
    { id: 36, title: "EXPERIMENT TRACKING", topics: ["Experiment Tracking"] },
    { id: 37, title: "HYPERPARAMETER SEARCH", topics: ["Hyperparameter Search"] },
    { id: 38, title: "DISTRIBUTED TRAINING", topics: ["Distributed Training"] },
    { id: 39, title: "PRODUCTION PIPELINES", topics: ["Production Data Pipelines"] },
    { id: 40, title: "REAL-TIME ML", topics: ["Real-time Machine Learning"] },
    { id: 41, title: "DATA & MODEL DRIFT", topics: ["Drift Detection & Handling"] },
    { id: 42, title: "SYNTHETIC DATA", topics: ["Synthetic Data Generation"] },
    { id: 43, title: "ML SECURITY", topics: ["Adversarial ML & Security"] },
    { id: 44, title: "GRAPH ML", topics: ["Knowledge Graphs & GNNs"] },
    { id: 45, title: "ADVANCED NLP", topics: ["Advanced NLP (Transformers)"] },
    { id: 46, title: "RECOMMENDATION SYSTEMS", topics: ["Recommendation Systems"] },
    { id: 47, title: "DEPLOYMENT", topics: ["Deployment Strategies"] },
    { id: 48, title: "EDGE AI", topics: ["ML on Edge (TinyML)"] }
  ];

  const devopsMenu = [
    { 
      id: 1, 
      title: "FOUNDATION & LINUX", 
      topics: ["What is DevOps?", "Linux Essentials for DevOps", "Linux Networking & Text Processing", "Bash Scripting"] 
    },
    { 
      id: 2, 
      title: "VERSION CONTROL (GIT)", 
      topics: ["Git Deep Dive", "Branching & Strategies", "Advanced Git (Rebase vs Merge)"] 
    },
    { 
      id: 3, 
      title: "CONTAINERIZATION", 
      topics: ["Why Docker?", "Essential Docker Commands", "Writing a Dockerfile", "Multi-Stage Builds", "Docker Compose"] 
    },
    { 
      id: 4, 
      title: "ORCHESTRATION (K8S)", 
      topics: ["Kubernetes Architecture", "Pods, Deployments, Services", "kubectl Cheat Sheet", "Helm (Package Manager)"] 
    },
    { 
      id: 5, 
      title: "INFRASTRUCTURE AS CODE", 
      topics: ["What is IaC?", "Terraform Basics", "The Terraform Workflow", "Terraform Modules"] 
    },
    { 
      id: 6, 
      title: "CONFIGURATION MANAGEMENT", 
      topics: ["Ansible Basics", "Playbooks & Modules", "Ansible Roles"] 
    },
    { 
      id: 7, 
      title: "CI/CD & AUTOMATION", 
      topics: ["CI/CD Pipelines (Jenkins/GHA)", "Deployment Strategies"] 
    },
    { 
      id: 8, 
      title: "OBSERVABILITY", 
      topics: ["Monitoring vs Observability", "Prometheus & Grafana"] 
    },
    { 
      id: 9, 
      title: "CLOUD COMPUTING", 
      topics: ["Cloud Basics (AWS/Azure)"] 
    },
    { 
      id: 10, 
      title: "SECURITY", 
      topics: ["DevSecOps"] 
    }
  ];

const wordMenu = [
    { id: 1, title: "GETTING STARTED", topics: ["Interface & Backstage View", "Text Basics & Selection", "Cut, Copy, Paste & Find"] },
    { id: 2, title: "FORMATTING", topics: ["Formatting Text", "Paragraph Formatting", "Bullets & Numbering", "Using Styles (Crucial)"] },
    { id: 3, title: "PAGE LAYOUT & DESIGN", topics: ["Page Setup", "Page & Section Breaks", "Headers & Footers"] },
    { id: 4, title: "TABLES", topics: ["Creating Tables", "Table Design & Layout"] },
    { id: 5, title: "GRAPHICS & MEDIA", topics: ["Pictures & Text Wrapping", "Shapes & SmartArt"] },
    { id: 6, title: "REFERENCES", topics: ["Table of Contents", "Citations & Bibliography", "Footnotes & Captions"] },
    { id: 7, title: "REVIEW", topics: ["Spelling & Thesaurus", "Track Changes & Comments"] },
    { id: 8, title: "MAILINGS", topics: ["Mail Merge"] },
    { id: 9, title: "ADVANCED FEATURES", topics: ["Macros", "Building Blocks (Quick Parts)", "Document Protection"] },
    { id: 10, title: "ULTIMATE SHORTCUTS LIST", topics: ["General & Document Shortcuts", "Text & Formatting Shortcuts", "Navigation & Selection", "Editing & Special", "Function Keys (F1-F12)"] }
  ];

  // Dynamic Content Selector logic
  const excelMenu = [
    { id: 1, title: "GETTING STARTED", topics: ["Interface & Workflow", "Workbooks vs Worksheets", "Cells, Rows & Columns", "Data Entry & Types"] },
    { id: 2, title: "FORMULAS & FUNCTIONS", topics: ["Formula Basics", "SUM, AVERAGE, COUNT", "Logical Functions (IF)", "VLOOKUP & XLOOKUP"] },
    { id: 3, title: "DATA VISUALIZATION", topics: ["Cell Formatting", "Conditional Formatting", "Charts & Graphs"] },
    { id: 4, title: "ADVANCED FEATURES", topics: ["Pivot Tables", "Data Validation (Dropdowns)", "Macros & VBA"] },
    { id: 5, title: "ULTIMATE SHORTCUTS LIST", topics: ["General & Workbook", "Data Entry & Editing", "Formatting & Numbers", "Navigation & Selection", "Formulas & Rows/Cols"] }
  ];



// --- CONTENT RENDERING LOGIC (EXACT FROM IMAGES) ---
  const renderTopicContent = () => {
     if (id === 'algorithms') {
    const algoContent = renderAlgorithmContent(activeTopic);
    if (algoContent) return algoContent;
  }
  if (id === 'javascript') {
    const jsContent = renderJSContent(activeTopic);
    if (jsContent) return jsContent;
  }
    if (id === 'python') {
    const pythonContent = renderPythonContent(activeTopic);
    if (pythonContent) return pythonContent;
  }
    if (id === 'java') {
    const javaContent = renderJavaContent(activeTopic);
    if (javaContent) return javaContent;
  }
  // For DSA and other sections, use switch-case
  switch (activeTopic) {
    case 'Introduction':
        return (
          <div className="space-y-6">
            <p className="text-lg text-slate-600 dark:text-slate-400">
              A <strong>Data Structure</strong> is a particular way of organizing data in a computer so that it can be used effectively. It provides a means to manage large amounts of data efficiently for uses such as large databases and internet indexing services.
            </p>
            <h2 className="text-2xl font-bold">Why are Data Structures important?</h2>
            <ul className="list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-400">
              <li><strong>Efficient Data Management:</strong> They allow for efficient storage, retrieval, and manipulation of data.</li>
              <li><strong>Algorithm Optimization:</strong> The choice of data structure is crucial for designing efficient algorithms. An appropriate data structure can significantly reduce the time and space complexity of an algorithm.</li>
              <li><strong>Real-world Problem Solving:</strong> They are fundamental in solving complex problems in areas like artificial intelligence, operating systems, and computer graphics.</li>
            </ul>
            <div className="p-6 border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-900/20 rounded-r-xl">
              <h3 className="font-bold text-blue-800 dark:text-blue-400">Analogy from CodeAlpha</h3>
              <p className="italic text-slate-600 dark:text-slate-400 text-sm">
                Think of a library. If books were unsorted, finding one would take forever. But by organizing them (e.g., by genre, then alphabetically), we create a structure that makes finding a book fast and efficient. Data structures do the same for data in a computer.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg border border-slate-200 dark:border-slate-800">
              <img src="https://images.unsplash.com/photo-1507842217343-583bb7270b66?q=80&w=1000" alt="Library" className="w-full h-64 object-cover" />
              <div className="p-3 bg-slate-100 dark:bg-slate-800 text-center text-xs italic text-slate-500">
                Visualizing Data Structures: Like organizing a chaotic library into efficient sections.
              </div>
            </div>
          </div>
        );

      case 'Asymptotic Analysis':
        return (
          <div className="space-y-6">
            <p className="text-lg text-slate-600 dark:text-slate-400">
              <strong>Asymptotic Analysis</strong> is the technique of estimating the performance of an algorithm as the input size grows without bound. It focuses on the growth rate of the running time or space requirements, ignoring constant factors and lower-order terms.
            </p>
            <h2 className="text-2xl font-bold">Three Cases of Analysis</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-5 rounded-2xl bg-green-50 dark:bg-green-900/20 border border-green-200">
                <h4 className="font-bold text-green-700">Best Case (Ω - Omega)</h4>
                <p className="text-xs text-green-600">The minimum time required for program execution. Represents the lower bound.</p>
              </div>
              <div className="p-5 rounded-2xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200">
                <h4 className="font-bold text-amber-700">Average Case (Θ - Theta)</h4>
                <p className="text-xs text-amber-600">The average time required, assuming random input. Represents a tight bound.</p>
              </div>
              <div className="p-5 rounded-2xl bg-red-50 dark:bg-red-900/20 border border-red-200">
                <h4 className="font-bold text-red-700">Worst Case (O - Big O)</h4>
                <p className="text-xs text-red-600">The maximum time required. This is the most common metric used as it guarantees performance.</p>
              </div>
            </div>
            <h3 className="text-xl font-bold">CodeAlpha Example: Linear Search</h3>
            <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300 shadow-2xl overflow-x-auto">
              <div className="flex gap-2 mb-4 border-b border-slate-800 pb-2 text-xs">
                <span className="w-3 h-3 rounded-full bg-red-500"></span>
                <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                <span className="w-3 h-3 rounded-full bg-green-500"></span>
                <span className="ml-2 uppercase">CPP</span>
              </div>
              <pre>{`// CodeAlpha: Linear Search Algorithm Analysis
// Best Case: O(1) -> Element is found at the first index.
// Average Case: O(n) -> Element is found somewhere in the middle.
// Worst Case: O(n) -> Element is at the last index or not present at all.

int search(int arr[], int n, int x) {
    for (int i = 0; i < n; i++) {
        if (arr[i] == x)
            return i;
    }
    return -1;
}`}</pre>
            </div>
          </div>
        );

      case 'Array Basics':
        return (
          <div className="space-y-6">
            <p className="text-lg text-slate-600 dark:text-slate-400">
              An <strong>Array</strong> is a collection of items stored at contiguous memory locations. The idea is to store multiple items of the same type together.
            </p>
            <div className="bg-slate-100 dark:bg-slate-800 p-6 rounded-xl font-mono text-sm text-center border-dashed border-2 border-slate-300">
              [ Index 0 | Index 1 | Index 2 | Index 3 ] <br/>
              [ Data A | Data B | Data C | Data D ]
            </div>
          </div>
        );

      case '2D & Multi-Dimensional Arrays':
        return (
          <div className="space-y-6">
            <p className="text-lg text-slate-600 dark:text-slate-400">
              A 2D array can be visualized as a grid or a table with rows and columns. It's essentially an array of arrays, useful for representing matrices or game boards. This concept can be extended to 3D or more dimensions.
            </p>
            <div className="bg-slate-100 dark:bg-slate-800 p-6 rounded-xl font-mono text-sm text-slate-500">
              <pre>{`// Visual Representation of matrix[2][3]
//      Col 0  Col 1  Col 2
// Row 0 [ 1  ,  2  ,  3  ]
// Row 1 [ 4  ,  5  ,  6  ]`}</pre>
            </div>
            <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300">
              <pre>{`int main() {
    int matrix[2][3] = { {1, 2, 3}, {4, 5, 6} };
    cout << "Element at [0][1]: " << matrix[0][1] << endl;
    return 0;
}`}</pre>
            </div>
          </div>
        );

      case 'Introduction to Linked Lists':
        return (
          <div className="space-y-6">
            <p className="text-lg text-slate-600 dark:text-slate-400">
              A <strong>Linked List</strong> is a linear data structure where elements (nodes) are not stored at contiguous memory locations. Instead, each node contains data and a pointer to the next node in the sequence.
            </p>
            <div className="bg-slate-100 dark:bg-slate-800 p-6 rounded-xl font-mono text-sm text-slate-500">
              <pre>{`// A Linked List is a chain of nodes
[Head]--->[Data|Next]--->[Data|Next]--->[Data|NULL]
  |          |              |              |
[10]----->[20]----------->[30]----------->NULL`}</pre>
            </div>
            <div className="p-5 border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-900/20 rounded-r-xl">
              <h4 className="font-bold">Why Linked List over Array?</h4>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Arrays have a fixed size. Linked Lists are dynamic; they can grow and shrink during runtime. Insertion and deletion are easier in Linked Lists (O(1)) compared to Arrays (O(n)).
              </p>
            </div>
          </div>
        );

      case 'Singly Linked List':
        return (
          <div className="space-y-6">
            <p className="text-lg text-slate-600 dark:text-slate-400">
              In a <strong>Singly Linked List</strong>, each node contains two parts: the data and a pointer to the next node. It allows traversal in one direction only.
            </p>
            <div className="flex justify-center p-6 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-200">
              <div className="flex items-center gap-4">
                <div className="border p-2 bg-white text-xs">HEAD ↓</div>
                <div className="flex border divide-x">
                  <div className="p-3 bg-slate-50">10</div><div className="p-3">NEXT ●</div>
                </div>
                <span>→</span>
                <div className="flex border divide-x">
                  <div className="p-3 bg-slate-50">20</div><div className="p-3">NEXT ●</div>
                </div>
                <span>→</span>
                <div className="flex border divide-x">
                  <div className="p-3 bg-slate-50">30</div><div className="p-3 text-red-500">NULL</div>
                </div>
              </div>
            </div>
            <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300 shadow-xl">
              <pre>{`struct Node {
    int data;
    Node* next;
    Node(int val) {
        data = val;
        next = NULL;
    }
};`}</pre>
            </div>
          </div>
        );

      case 'Doubly Linked List':
        return (
          <div className="space-y-6">
            <p className="text-lg text-slate-600 dark:text-slate-400">
              A <strong>Doubly Linked List (DLL)</strong> contains an extra pointer, typically called <em>prev</em>, together with the next pointer and data. This allows traversal in both forward and backward directions.
            </p>
            <div className="flex justify-center p-6 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-200 overflow-x-auto text-xs">
              <div className="flex items-center gap-2">
                <span className="text-red-500">NULL</span>
                <span>←</span>
                <div className="flex border divide-x">
                   <div className="p-2">PREV ●</div><div className="p-2 bg-slate-100">10</div><div className="p-2">NEXT ●</div>
                </div>
                <span>↔</span>
                <div className="flex border divide-x">
                   <div className="p-2">PREV ●</div><div className="p-2 bg-slate-100">20</div><div className="p-2">NEXT ●</div>
                </div>
                <span>→</span>
                <span className="text-red-500">NULL</span>
              </div>
            </div>
            <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300 shadow-xl">
              <pre>{`struct Node {
    int data;
    Node* next;
    Node* prev;
    Node(int val) {
        data = val;
        next = prev = NULL;
    }
};`}</pre>
            </div>
          </div>
        );

      case 'Circular Linked List':
        return (
          <div className="space-y-6">
            <p className="text-lg text-slate-600 dark:text-slate-400">
              A <strong>Circular Linked List</strong> is a variation where the last node points back to the first node instead of NULL, forming a circle. This is useful for applications that require a cycle, like a round-robin scheduler.
            </p>
            <div className="flex justify-center p-6 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-200 text-xs">
               <div className="flex items-center gap-4">
                 <div className="flex border divide-x"><div className="p-3">10</div><div className="p-3">●</div></div><span>→</span>
                 <div className="flex border divide-x"><div className="p-3">20</div><div className="p-3">●</div></div><span>→</span>
                 <div className="flex border divide-x"><div className="p-3">30</div><div className="p-3">●</div></div>
               </div>
            </div>
            <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300">
              <pre>{`void printList(Node* head) {
    if(head == NULL) return;
    Node* temp = head;
    do {
        cout << temp->data << " ";
        temp = temp->next;
    } while(temp != head);
}`}</pre>
            </div>
          </div>
        );

      case 'Stack':
        return (
          <div className="space-y-6">
            <p className="text-lg text-slate-600 dark:text-slate-400">
              A <strong>Stack</strong> is an abstract data type that follows the <strong>LIFO (Last-In, First-Out)</strong> principle. The last element added to the stack will be the first one to be removed. Think of it like a stack of plates.
            </p>
            <div className="bg-slate-100 dark:bg-slate-800 p-6 rounded-xl font-mono text-sm text-slate-500">
              <pre>{`// PUSH operation adds an element to the top
// POP operation removes the top element

// Push "B"    | B | ← Top
//             | A |
// Pop         | A | ← Top`}</pre>
            </div>
            <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300">
              <pre>{`int main() {
    stack<string> s;
    s.push("CodeAlpha");
    s.push("Internship");
    cout << "Top Element: " << s.top() << endl; // Internship
    s.pop();
    cout << "New Top: " << s.top() << endl; // CodeAlpha
    return 0;
}`}</pre>
            </div>
          </div>
        );

      case 'Queue':
        return (
          <div className="space-y-6">
            <p className="text-lg text-slate-600 dark:text-slate-400">
              A <strong>Queue</strong> is an abstract data type that follows the <strong>FIFO (First-In, First-Out)</strong> principle. The first element added is the first one to be removed, like a queue of people waiting in line.
            </p>
            <div className="bg-slate-100 dark:bg-slate-800 p-6 rounded-xl font-mono text-sm text-slate-500">
              <pre>{`// ENQUEUE adds an element to the rear
// DEQUEUE removes an element from the front

// Enqueue "A", "B", "C"
// Front → [ A | B | C ] ← Rear

// Dequeue (removes "A")
// Front → [ B | C ] ← Rear`}</pre>
            </div>
            <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300">
              <pre>{`int main() {
    queue<int> q;
    q.push(10);
    q.push(20);
    cout << "Front: " << q.front() << endl; // 10
    q.pop();
    cout << "New Front: " << q.front() << endl; // 20
    return 0;
}`}</pre>
            </div>
          </div>
        );

        case 'Binary Tree':
        return (
          <div className="space-y-6">
            <p className="text-lg text-slate-600 dark:text-slate-400">
              A <strong>Binary Tree</strong> is a hierarchical data structure in which each node has at most two children, referred to as the left child and the right child.
            </p>
            <div className="bg-slate-100 dark:bg-slate-800 p-6 rounded-xl font-mono text-sm text-center border border-slate-200">
              <pre>{`      (1)       <- Root
     /   \\
   (2)   (3)    <- Children
   / \\
 (4) (5)        <- Leaves`}</pre>
            </div>
          </div>
        );

      case 'Binary Search Tree':
        return (
          <div className="space-y-6">
            <p className="text-lg text-slate-600 dark:text-slate-400">
              A <strong>Binary Search Tree (BST)</strong> is a special type of binary tree with a specific ordering property: for any given node, all values in its left subtree are less than the node's value, and all values in its right subtree are greater. This makes searching very efficient (O(log n) on average).
            </p>
            <div className="bg-slate-100 dark:bg-slate-800 p-4 rounded-xl font-mono text-sm">
              <pre>{`// BST Property
    (Root)
      10
     /  \\
 (< 10)  (> 10)
   5      15`}</pre>
            </div>
            <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300">
              <pre>{`// CodeAlpha: Search in a BST
Node* search(Node* root, int key) {
    if (root == NULL || root->data == key) {
        return root; // Found or not in tree
    }
    if (root->data < key) {
        return search(root->right, key);
    }
    return search(root->left, key);
}`}</pre>
            </div>
          </div>
        );

      case 'Tree Traversals':
        return (
          <div className="space-y-6">
            <p className="text-lg text-slate-600 dark:text-slate-400">
              Traversal is the process of visiting each node in a tree exactly once. There are three main types of Depth-First Traversals:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-400">
              <li><strong>Inorder (Left, Root, Right):</strong> In a BST, this traversal visits nodes in ascending order.</li>
              <li><strong>Preorder (Root, Left, Right):</strong> Useful for creating a copy of the tree.</li>
              <li><strong>Postorder (Left, Right, Root):</strong> Used for deleting the tree from memory.</li>
            </ul>
            <div className="bg-slate-100 dark:bg-slate-800 p-4 rounded-xl font-mono text-sm">
              <pre>{`// Traversal Orders:
// Inorder:   5, 10, 15
// Preorder:  10, 5, 15
// Postorder: 5, 15, 10`}</pre>
            </div>
            <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300">
              <pre>{`void inorder(struct Node* node) {
    if (node == NULL) return;
    inorder(node->left);   // Visit left
    cout << node->data;    // Visit root
    inorder(node->right);  // Visit right
}`}</pre>
            </div>
          </div>
        );

      case 'Heaps (Min/Max)':
        return (
          <div className="space-y-6">
            <p className="text-lg text-slate-600 dark:text-slate-400">
              A <strong>Heap</strong> is a specialized tree-based data structure that satisfies the heap property. In a <strong>Max-Heap</strong>, the parent node is always greater than or equal to its children. In a <strong>Min-Heap</strong>, the parent is always less than or equal to its children.
            </p>
            <div className="bg-slate-100 dark:bg-slate-800 p-4 rounded-xl font-mono text-sm">
              <pre>{`// A Max-Heap Structure
//      (50)
//     /    \\
//   (30)   (20)
//   /  \\
// (10) (15)`}</pre>
            </div>
            <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300 shadow-2xl">
              <pre>{`int main() {
    priority_queue<int> max_heap;
    max_heap.push(30);
    max_heap.push(50);
    cout << "Max element: " << max_heap.top(); // Outputs 50
    return 0;
}`}</pre>
            </div>
          </div>
        );

      case 'Introduction to Graphs':
        return (
          <div className="space-y-6">
            <p className="text-lg text-slate-600 dark:text-slate-400">
              A <strong>Graph</strong> is a non-linear data structure consisting of nodes (vertices) and edges that connect these nodes. Graphs are used to model relationships and networks, such as social networks and the internet.
            </p>
            <div className="bg-slate-100 dark:bg-slate-800 p-4 rounded-xl font-mono text-sm">
              <pre>{`// A simple undirected graph
//  0 --- 1
//  |   /
//  | /
//  2`}</pre>
            </div>
            <h2 className="text-2xl font-bold">Representation</h2>
            <ul className="list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-400">
              <li><strong>Adjacency Matrix:</strong> A 2D array where `matrix[i][j] = 1` if there's an edge. Uses O(V^2) space.</li>
              <li><strong>Adjacency List:</strong> An array of linked lists, more space-efficient for sparse graphs.</li>
            </ul>
          </div>
        );

      case 'BFS Traversal':
        return (
          <div className="space-y-6">
            <p className="text-lg text-slate-600 dark:text-slate-400">
              BFS is a graph traversal algorithm that explores neighbor nodes first, before moving to the next level neighbors. It uses a <strong>Queue</strong> data structure.
            </p>
            <div className="bg-slate-100 dark:bg-slate-800 p-4 rounded-xl font-mono text-sm">
              <pre>{`// BFS Order starting from A:
// A, B, C, D, E`}</pre>
            </div>
            <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300">
              <pre>{`// CodeAlpha: BFS Concept
// 1. Create a queue and a visited array.
// 2. Enqueue the starting node.
// 3. While queue is not empty:
//    a. Dequeue node 'u'.
//    b. For all unvisited neighbors 'v':
//       Mark 'v' visited and Enqueue 'v'.`}</pre>
            </div>
          </div>
        );

      case 'DFS Traversal':
        return (
          <div className="space-y-6">
            <p className="text-lg text-slate-600 dark:text-slate-400">
              DFS explores as deep as possible along each branch before backtracking. It uses a <strong>Stack</strong> (or recursion). It is used for cycle detection and solving puzzles like mazes.
            </p>
            <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300">
              <pre>{`// CodeAlpha: DFS Recursive Concept
void DFS(int u, bool visited[]) {
    visited[u] = true;
    cout << u << " ";
    for (int v : adj[u]) {
        if (!visited[v]) DFS(v, visited);
    }
}`}</pre>
            </div>
          </div>
        );

      case 'Hashing':
        return (
          <div className="space-y-6">
            <p className="text-lg text-slate-600 dark:text-slate-400">
              <strong>Hashing</strong> is a technique used to map data of any size to a fixed-size value, called a hash code or hash value. It provides O(1) average time complexity for search, insertion, and deletion.
            </p>
            <div className="bg-slate-100 dark:bg-slate-800 p-4 rounded-xl font-mono text-sm">
              <pre>{`// Key -> Hash Function -> Index
// "CodeAlpha" -> [Function] -> 7`}</pre>
            </div>
            <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300">
              <pre>{`#include <unordered_map>
int main() {
    unordered_map<string, int> internship_slots;
    internship_slots["Web Development"] = 150;
    cout << internship_slots["Web Development"]; // 150
    return 0;
}`}</pre>
            </div>
          </div>
        );
      default:
        return (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-20 h-20 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-6"><Search className="w-10 h-10 text-slate-400" /></div>
            <h3 className="text-xl font-bold">Content Coming Soon</h3>
            <p className="text-slate-500 mt-2 text-sm">Detailed guide for <strong>{activeTopic}</strong> is in progress.</p>
          </div>
        );
    }
  };

  // --- DYNAMIC CONTENT SELECTION ---
  let currentMenu, courseTitle, courseIcon, accentColor, glowColor;
  if (id === 'algorithms') {
    currentMenu = algorithmsMenu; courseTitle = 'Algorithms'; courseIcon = "⚙️"; accentColor = "text-orange-600"; glowColor = "shadow-orange-500/20";
  } else if (id === 'javascript') {
    currentMenu = jsMenu; courseTitle = 'JavaScript'; courseIcon = "🌐"; accentColor = "text-yellow-600"; glowColor = "shadow-yellow-500/20";
  } else if (id === 'python') {
    currentMenu = pythonMenu; courseTitle = 'Python'; courseIcon = "🐍"; accentColor = "text-sky-600"; glowColor = "shadow-sky-500/20";
  } else if (id === 'java') {
    currentMenu = javaMenu; courseTitle = 'Java'; courseIcon = "☕"; accentColor = "text-red-600"; glowColor = "shadow-red-500/20";
  } else if (id === 'c-programming') {
    currentMenu = cMenu; courseTitle = 'C Programming'; courseIcon = ">_"; accentColor = "text-blue-600"; glowColor = "shadow-blue-500/20";
  } else if (id === 'cpp-programming') {
    currentMenu = cppMenu; courseTitle = 'C++ Programming'; courseIcon = "</>"; accentColor = "text-indigo-600"; glowColor = "shadow-indigo-500/20";
  } else if (id === 'php') {
    currentMenu = phpMenu; courseTitle = 'PHP'; courseIcon = "🐘"; accentColor = "text-violet-600"; glowColor = "shadow-violet-500/20";
  } else if (id === 'ml-data-science') {
    currentMenu = mlMenu; courseTitle = 'ML & Data Science'; courseIcon = "🛢️"; accentColor = "text-teal-600"; glowColor = "shadow-teal-500/20";
  } else if (id === 'devops') {
    currentMenu = devopsMenu; courseTitle = 'DevOps'; courseIcon = "☁️"; accentColor = "text-cyan-600"; glowColor = "shadow-cyan-500/20";
  } else if (id === 'microsoft-word-2021') {
    currentMenu = wordMenu; courseTitle = 'Microsoft Word 2021'; courseIcon = "📄"; accentColor = "text-blue-700"; glowColor = "shadow-blue-500/20";
  } else if (id === 'microsoft-excel-2021') {
    currentMenu = excelMenu; courseTitle = 'Microsoft Excel 2021'; courseIcon = "📊"; accentColor = "text-green-600"; glowColor = "shadow-green-500/20";
  } else {
    currentMenu = dsaMenu; courseTitle = 'Data Structures'; courseIcon = "📂"; accentColor = "text-purple-600"; glowColor = "shadow-purple-500/20";
  }

  // --- NAVIGATION HELPERS ---
  const allTopics = currentMenu.flatMap(section => section.topics.map(topic => ({ topic })));
  const currentIndex = allTopics.findIndex(t => t.topic === activeTopic);
  const goToPrevious = () => currentIndex > 0 && setActiveTopic(allTopics[currentIndex - 1].topic);
  const goToNext = () => currentIndex < allTopics.length - 1 && setActiveTopic(allTopics[currentIndex + 1].topic);

  useEffect(() => {
    if (currentMenu && currentMenu.length > 0) setActiveTopic(currentMenu[0].topics[0]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-950 pt-[80px]">
      {/* SIDEBAR */}
      <aside className={`fixed lg:sticky top-[80px] h-[calc(100vh-80px)] w-[320px] bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-r border-slate-200 dark:border-slate-800 flex flex-col transition-all duration-500 z-50 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="p-6 pb-2">
          <div className="flex items-center gap-4 mb-6">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-2xl shadow-lg bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 ${glowColor}`}>{courseIcon}</div>
            <div>
              <h2 className="text-lg font-black text-slate-800 dark:text-white leading-tight uppercase tracking-tight">{courseTitle}</h2>
              <span className="text-[10px] font-bold text-slate-400 tracking-widest uppercase">Curriculum</span>
            </div>
          </div>
          <div className="relative group">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-primary transition-colors" />
            <input type="text" placeholder="Quick search..." className="w-full bg-slate-100 dark:bg-slate-800/50 border border-transparent focus:border-primary/30 rounded-xl py-2.5 pl-10 pr-4 text-sm outline-none transition-all" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-6 no-scrollbar" style={{ scrollbarWidth: 'none' }}>
          <nav className="space-y-8">
            {currentMenu.map((section) => (
              <div key={section.id}>
                <h3 className="px-4 text-[11px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[2px] mb-4">{section.id}. {section.title}</h3>
                <ul className="space-y-1">
                  {section.topics.map((topic) => (
                    <li key={topic}>
                      <button onClick={() => { setActiveTopic(topic); setIsSidebarOpen(false); }} className={`w-full text-left px-4 py-3 rounded-xl text-[14px] transition-all duration-300 flex items-center justify-between group ${activeTopic === topic ? 'bg-white dark:bg-slate-800 shadow-md ring-1 ring-slate-200 dark:ring-slate-700 font-bold' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200/50 dark:hover:bg-slate-800/50'}`}>
                        <span className={activeTopic === topic ? accentColor : ''}>{topic}</span>
                        {activeTopic === topic && <div className={`w-1.5 h-1.5 rounded-full animate-pulse ${accentColor.replace('text', 'bg')}`}></div>}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </div>
        <div className="p-4 border-t border-slate-100 dark:border-slate-800">
          <Link to="/tutorials" className="flex items-center justify-center gap-2 w-full py-3 bg-white dark:bg-slate-800 border border-slate-200 rounded-xl text-sm font-bold text-slate-600 hover:shadow-md transition-all">
            <ChevronLeft className="w-4 h-4" /> Exit to Library
          </Link>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main className="flex-1 p-6 lg:p-16 max-w-5xl mx-auto w-full">
        <div className="flex items-center justify-between mb-12">
          <div className="flex items-center text-[13px] text-slate-400 font-medium">
            <Link to="/tutorials" className="hover:text-primary transition-colors">Tutorials</Link>
            <span className="mx-3 opacity-30">/</span>
            <span className="text-slate-500">{courseTitle}</span>
            <span className="mx-3 opacity-30">/</span>
            <span className={`${accentColor} font-bold`}>{activeTopic}</span>
          </div>
          <button className="p-2.5 bg-white dark:bg-slate-900 border border-slate-200 rounded-xl text-slate-500 hover:border-primary/50 transition-all"><Share2 className="w-4 h-4" /></button>
        </div>

        <article className="relative mb-12">
           <div className="absolute -left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-primary to-transparent rounded-full hidden lg:block"></div>
           <div className="flex items-center gap-2 mb-4 text-[10px] font-bold text-primary uppercase tracking-[3px]">
             <Clock className="w-3 h-3" /> 10 Min Read
           </div>
           <h1 className="text-4xl md:text-6xl font-black text-slate-900 dark:text-white tracking-tight leading-none mb-10">{activeTopic}</h1>
           
           {/* DYNAMIC CONTENT AREA */}
           <div className="min-h-[400px]">
             {renderTopicContent()}
           </div>
        </article>

        {/* BOTTOM NAV */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-10 border-t border-slate-200 dark:border-slate-800">
            <button onClick={goToPrevious} disabled={currentIndex === 0} className={`p-6 rounded-[24px] bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 hover:border-primary/50 transition-all text-left ${currentIndex === 0 ? 'opacity-30 cursor-not-allowed' : ''}`}>
               <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">← PREVIOUS</span>
               <h4 className="text-lg font-bold text-slate-800 dark:text-white">{currentIndex > 0 ? allTopics[currentIndex - 1].topic : "Introduction"}</h4>
            </button>
            <button onClick={goToNext} disabled={currentIndex === allTopics.length - 1} className={`p-6 rounded-[24px] bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 hover:border-primary/50 transition-all text-right ${currentIndex === allTopics.length - 1 ? 'opacity-30 cursor-not-allowed' : ''}`}>
               <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">NEXT →</span>
               <h4 className="text-lg font-bold text-slate-800 dark:text-white">{currentIndex < allTopics.length - 1 ? allTopics[currentIndex + 1].topic : "End of Module"}</h4>
            </button>
        </div>
      </main>

      {/* MOBILE BUTTON */}
      <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="lg:hidden fixed bottom-8 right-8 z-[100] bg-primary text-white h-14 w-14 rounded-2xl shadow-2xl flex items-center justify-center hover:scale-110 active:scale-95 transition-transform">
        {isSidebarOpen ? <ChevronLeft /> : <BookOpen className="w-6 h-6" />}
      </button>
    </div>
  );
};

export default SubTutorial;